import { useMemo, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Check } from 'lucide-react';
import {
  applySmartCleanupRule,
  cancelVoidCleanup,
  listBalanceCleanupTickets,
  listCleanupBatches,
  markPaidCleanup,
  previewSmartCleanupRule,
  recalculateCleanup,
  undoCleanupBatch,
  writeOffCleanup,
  type BalanceCleanupRow,
} from '@/lib/api/data-cleanup.js';
import { formatCurrency, formatDate, formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { cn } from '@/lib/utils.js';
import { ManagerOverrideModal } from '@/components/common/ManagerOverrideModal.js';
import { useManagerOverride } from '@/hooks/useManagerOverride.js';
import { Pagination } from '@/components/common/Pagination.js';
import { fromDateTimeLocalValue } from '@/lib/datetime-local.js';

const STATUS_OPTIONS = [
  'intake',
  'in_queue',
  'in_repair',
  'waiting_parts',
  'ready',
  'closed',
  'cancelled',
  'voided',
  'warranty',
  'pending_pay',
];

type BulkKind = 'mark_paid' | 'write_off' | 'cancel' | 'void' | 'recalc' | null;

/** Matches server resolveCleanupPaidAt default (closed_at ?? created_at). */
function resolvePreviewPaidAt(
  row: BalanceCleanupRow,
  batchOverrideLocal: string,
): { date: Date; source: 'batch' | 'closed_at' | 'created_at' } {
  if (batchOverrideLocal.trim()) {
    try {
      return { date: new Date(fromDateTimeLocalValue(batchOverrideLocal)), source: 'batch' };
    } catch {
      /* fall through */
    }
  }
  if (row.closedAt) return { date: new Date(row.closedAt), source: 'closed_at' };
  return { date: new Date(row.createdAt), source: 'created_at' };
}

function CopyTicketNumber({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-mono text-sm font-bold text-primary hover:underline"
      title="Copy ticket number"
      onClick={async (e) => {
        e.stopPropagation();
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        } catch {
          toastError('Could not copy');
        }
      }}
    >
      {value}
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

export function DataCleanupBalancesPage() {
  const user = useAuthStore((s) => s.user);
  const isTenantAdmin = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const queryClient = useQueryClient();
  const { requireOverride, modalProps } = useManagerOverride();

  const [tab, setTab] = useState<'grid' | 'history'>('grid');
  const [statuses, setStatuses] = useState<string[]>([]);
  const [hasPayments, setHasPayments] = useState<'yes' | 'no' | 'any'>('any');
  const [importedOnly, setImportedOnly] = useState(true);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkKind, setBulkKind] = useState<BulkKind>(null);
  const [previewExpand, setPreviewExpand] = useState(false);
  const [markMethod, setMarkMethod] = useState('other');
  const [batchPaidAtLocal, setBatchPaidAtLocal] = useState('');
  const [amountByTicket, setAmountByTicket] = useState<Record<string, string>>({});
  const [writeOffReason, setWriteOffReason] = useState('');
  const [ackClosed, setAckClosed] = useState(false);
  const [smartPreview, setSmartPreview] = useState<{
    rule: 'cancelled_no_payments' | 'cancelled_with_payments';
    ticketCount: number;
    totalAmount: string;
    tickets: Array<{ id: string; ticketNumber: string; balanceDue: string }>;
  } | null>(null);

  const listQ = useQuery({
    queryKey: ['data-cleanup-balances', statuses, hasPayments, importedOnly, customerSearch, page],
    queryFn: () => {
      const params: Parameters<typeof listBalanceCleanupTickets>[0] = {
        hasPayments,
        importedOnly,
        page,
        pageSize: 50,
      };
      if (statuses.length) params.statuses = statuses;
      if (customerSearch) params.customerSearch = customerSearch;
      return listBalanceCleanupTickets(params);
    },
    enabled: isTenantAdmin && tab === 'grid',
  });

  const historyQ = useQuery({
    queryKey: ['data-cleanup-batches'],
    queryFn: listCleanupBatches,
    enabled: isTenantAdmin && tab === 'history',
  });

  const rows = listQ.data?.data ?? [];
  const meta = listQ.data?.meta;

  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.id)),
    [rows, selected],
  );
  const selectedBalance = selectedRows.reduce((s, r) => s + Number(r.balanceDue), 0);
  const markPaidPreviewTotal = useMemo(() => {
    if (bulkKind !== 'mark_paid') return selectedBalance;
    return selectedRows.reduce((sum, r) => {
      const raw = amountByTicket[r.id];
      const n = raw != null && raw !== '' ? Number(raw) : Number(r.balanceDue);
      if (!Number.isFinite(n) || n <= 0) return sum;
      return sum + Math.min(n, Number(r.balanceDue));
    }, 0);
  }, [bulkKind, selectedRows, amountByTicket, selectedBalance]);

  function startMarkPaid() {
    const next: Record<string, string> = {};
    for (const r of selectedRows) {
      next[r.id] = Number(r.balanceDue).toFixed(2);
    }
    setAmountByTicket(next);
    setBatchPaidAtLocal('');
    setPreviewExpand(true);
    setBulkKind('mark_paid');
  }

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['data-cleanup-balances'] });
    void queryClient.invalidateQueries({ queryKey: ['data-cleanup-batches'] });
    setSelected(new Set());
    setBulkKind(null);
    setSmartPreview(null);
    setAckClosed(false);
    setBatchPaidAtLocal('');
    setAmountByTicket({});
  }

  function toggleStatus(s: string) {
    setPage(1);
    setStatuses((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  }

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }

  function onGridKey(e: KeyboardEvent<HTMLTableElement>) {
    if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      toggleAll();
    }
  }

  async function withPin<T>(fn: (authorizedBy: string) => Promise<T>, title: string): Promise<T | null> {
    const result = await requireOverride({
      action: 'data_cleanup',
      title,
      description: 'Confirm with a manager PIN to apply this financial cleanup.',
      requiredRole: 'tenant_admin',
      context: { reason: title },
    });
    if (!result.approved) return null;
    return fn(result.authorizedBy.id);
  }

  const markPaidMut = useMutation({
    mutationFn: async () => {
      const ids = selectedRows.map((r) => r.id);
      const amounts: Record<string, number> = {};
      for (const r of selectedRows) {
        const raw = amountByTicket[r.id];
        const n = raw != null && raw !== '' ? Number(raw) : Number(r.balanceDue);
        if (!Number.isFinite(n) || n <= 0) {
          throw Object.assign(new Error(`Invalid amount for ${r.ticketNumber}`), { code: 'INVALID_AMOUNT' });
        }
        const capped = Math.min(n, Number(r.balanceDue));
        if (Math.abs(capped - Number(r.balanceDue)) > 0.009) {
          amounts[r.id] = Math.round(capped * 100) / 100;
        }
      }
      return withPin(
        (authorizedBy) => {
          const body: Parameters<typeof markPaidCleanup>[0] = {
            ticketIds: ids,
            method: markMethod,
            useClosedAt: true,
            managerOverrideAuthorizedBy: authorizedBy,
          };
          if (ackClosed) body.acknowledgeClosedSession = true;
          if (Object.keys(amounts).length) body.amounts = amounts;
          if (batchPaidAtLocal.trim()) {
            body.paidAt = fromDateTimeLocalValue(batchPaidAtLocal);
            body.useClosedAt = false;
          }
          return markPaidCleanup(body);
        },
        'Mark paid (admin backfill)',
      );
    },
    onSuccess: (data) => {
      if (!data) return;
      toastSuccess(`Marked ${data.ticketCount} tickets paid · ${formatCurrency(data.totalAmount)}`);
      invalidate();
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      if (parsed.code === 'CLOSED_SESSION_WARNING') {
        setAckClosed(false);
        toastError(parsed.message + ' Check acknowledgement and retry.');
        return;
      }
      toastError(parsed.message);
    },
  });

  async function runBulk() {
    if (!bulkKind || selectedRows.length === 0) return;
    try {
      if (bulkKind === 'mark_paid') {
        await markPaidMut.mutateAsync();
        return;
      }
      if (bulkKind === 'write_off') {
        if (!writeOffReason.trim()) {
          toastError('Write-off reason is required');
          return;
        }
        const data = await withPin(
          (authorizedBy) =>
            writeOffCleanup({
              ticketIds: selectedRows.map((r) => r.id),
              reason: writeOffReason.trim(),
              managerOverrideAuthorizedBy: authorizedBy,
            }),
          'Write off balances',
        );
        if (data) {
          toastSuccess(`Wrote off ${data.ticketCount} · ${formatCurrency(data.totalAmount)}`);
          invalidate();
        }
        return;
      }
      if (bulkKind === 'cancel' || bulkKind === 'void') {
        const data = await withPin(
          (authorizedBy) =>
            cancelVoidCleanup({
              ticketIds: selectedRows.map((r) => r.id),
              status: bulkKind === 'void' ? 'voided' : 'cancelled',
              managerOverrideAuthorizedBy: authorizedBy,
            }),
          bulkKind === 'void' ? 'Void tickets' : 'Cancel tickets',
        );
        if (data) {
          toastSuccess(`Updated ${data.ticketCount} · cleared ${formatCurrency(data.totalAmount)}`);
          invalidate();
        }
        return;
      }
      if (bulkKind === 'recalc') {
        const data = await withPin(
          (authorizedBy) =>
            recalculateCleanup({
              ticketIds: selectedRows.map((r) => r.id),
              managerOverrideAuthorizedBy: authorizedBy,
            }),
          'Recalculate from payments',
        );
        if (data) {
          toastSuccess(`Recalculated ${data.ticketCount} tickets`);
          invalidate();
        }
      }
    } catch (err) {
      toastError(parseApiError(err).message);
    }
  }

  async function loadSmart(rule: 'cancelled_no_payments' | 'cancelled_with_payments') {
    try {
      const preview = await previewSmartCleanupRule(rule);
      setSmartPreview({
        rule,
        ticketCount: preview.ticketCount,
        totalAmount: preview.totalAmount,
        tickets: preview.tickets,
      });
    } catch (err) {
      toastError(parseApiError(err).message);
    }
  }

  async function applySmart() {
    if (!smartPreview) return;
    try {
      const data = await withPin(
        (authorizedBy) =>
          applySmartCleanupRule(smartPreview.rule, { managerOverrideAuthorizedBy: authorizedBy }),
        'Apply smart cleanup rule',
      );
      if (data) {
        toastSuccess(
          `Applied to ${data.ticketCount} tickets${data.totalAmount ? ` · ${formatCurrency(data.totalAmount)}` : ''}`,
        );
        invalidate();
      }
    } catch (err) {
      toastError(parseApiError(err).message);
    }
  }

  if (!isTenantAdmin) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        Tenant admin access required for data cleanup.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-950">Balance cleanup</h1>
        <p className="mt-1 text-sm text-slate-500">
          Correct false balances left by imports. Every change goes through normal payment /
          write-off paths and is undoable.{' '}
          <Link to="/settings/data-cleanup/parts-costs" className="font-semibold text-primary hover:underline">
            Parts cost backfill
          </Link>
          {' · '}
          <Link to="/settings/data-cleanup/inventory-duplicates" className="font-semibold text-primary hover:underline">
            Inventory duplicates
          </Link>
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          className={cn(
            'rounded-xl px-3 py-1.5 text-sm font-semibold',
            tab === 'grid' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
          )}
          onClick={() => setTab('grid')}
        >
          Open balances
        </button>
        <button
          type="button"
          className={cn(
            'rounded-xl px-3 py-1.5 text-sm font-semibold',
            tab === 'history' ? 'bg-primary text-white' : 'bg-slate-100 text-slate-700',
          )}
          onClick={() => setTab('history')}
        >
          History / Undo
        </button>
      </div>

      {tab === 'history' ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Operation</th>
                <th className="px-4 py-3">Tickets</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {(historyQ.data ?? []).map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-4 py-3">{formatDate(b.performedAt)}</td>
                  <td className="px-4 py-3 font-medium">{prettyLabel(b.operation)}</td>
                  <td className="px-4 py-3">{b.ticketCount}</td>
                  <td className="px-4 py-3">{b.totalAmount ? formatCurrency(b.totalAmount) : '—'}</td>
                  <td className="max-w-xs truncate px-4 py-3 text-slate-600">{b.reason ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {b.reversedAt ? (
                      <span className="text-xs font-semibold text-slate-400">Reversed</span>
                    ) : (
                      <button
                        type="button"
                        className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold hover:bg-slate-50"
                        onClick={async () => {
                          try {
                            await withPin(
                              (authorizedBy) => undoCleanupBatch(b.id, authorizedBy),
                              'Undo cleanup batch',
                            );
                            toastSuccess('Batch reversed');
                            invalidate();
                          } catch (err) {
                            toastError(parseApiError(err).message);
                          }
                        }}
                      >
                        Undo
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(historyQ.data ?? []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    No cleanup batches yet
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <label className="text-sm">
              <span className="font-medium text-slate-700">Customer / ticket</span>
              <div className="mt-1 flex gap-2">
                <input
                  className="rounded-xl border border-slate-300 px-3 py-2"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setCustomerSearch(searchInput);
                      setPage(1);
                    }
                  }}
                />
                <button
                  type="button"
                  className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => {
                    setCustomerSearch(searchInput);
                    setPage(1);
                  }}
                >
                  Search
                </button>
              </div>
            </label>
            <label className="text-sm">
              <span className="font-medium text-slate-700">Payments</span>
              <select
                className="mt-1 block rounded-xl border border-slate-300 px-3 py-2"
                value={hasPayments}
                onChange={(e) => {
                  setHasPayments(e.target.value as 'yes' | 'no' | 'any');
                  setPage(1);
                }}
              >
                <option value="any">Any</option>
                <option value="yes">Has payment rows</option>
                <option value="no">No payment rows</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={importedOnly}
                onChange={(e) => {
                  setImportedOnly(e.target.checked);
                  setPage(1);
                }}
              />
              Imported only
            </label>
            <div className="ml-auto text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{meta?.total ?? 0}</span> tickets ·{' '}
              <span className="font-semibold text-slate-900">
                {formatCurrency(meta?.balanceSum ?? 0)}
              </span>{' '}
              open ·{' '}
              <span className="font-semibold text-amber-700">{meta?.cancelledCount ?? 0}</span>{' '}
              cancelled/voided in view
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleStatus(s)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-semibold',
                  statuses.includes(s) ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600',
                )}
              >
                {prettyLabel(s)}
              </button>
            ))}
            {statuses.length ? (
              <button
                type="button"
                className="text-xs font-semibold text-slate-500 underline"
                onClick={() => setStatuses([])}
              >
                Clear statuses
              </button>
            ) : null}
          </div>

          <div className="rounded-2xl border border-indigo-100 bg-indigo-50/60 p-4">
            <h2 className="text-sm font-bold text-indigo-950">Smart rules</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-900"
                onClick={() => void loadSmart('cancelled_no_payments')}
              >
                Zero cancelled with NO payments
              </button>
              <button
                type="button"
                className="rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-900"
                onClick={() => void loadSmart('cancelled_with_payments')}
              >
                Zero remaining on cancelled WITH payments
              </button>
            </div>
            {smartPreview ? (
              <div className="mt-3 rounded-xl border border-indigo-200 bg-white p-3 text-sm">
                <p className="font-semibold text-slate-900">
                  {smartPreview.ticketCount} tickets · {formatCurrency(smartPreview.totalAmount)}
                  {smartPreview.rule === 'cancelled_with_payments'
                    ? ' will be written off (payments kept)'
                    : ' will be written off (no payments)'}
                </p>
                <button
                  type="button"
                  className="mt-1 text-xs font-semibold text-primary"
                  onClick={() => setPreviewExpand((v) => !v)}
                >
                  {previewExpand ? 'Hide list' : 'Show ticket list'}
                </button>
                {previewExpand ? (
                  <ul className="mt-2 max-h-40 overflow-auto text-xs text-slate-600">
                    {smartPreview.tickets.map((t) => (
                      <li key={t.id}>
                        {t.ticketNumber} · {formatCurrency(t.balanceDue)}
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                    onClick={() => void applySmart()}
                  >
                    Apply with manager PIN
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                    onClick={() => setSmartPreview(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {selected.size > 0 ? (
            <div className="sticky top-2 z-10 space-y-3 rounded-2xl border border-slate-300 bg-white p-4 shadow-lg">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-bold text-slate-900">
                  {selected.size} selected · {formatCurrency(selectedBalance)}
                </span>
                <button
                  type="button"
                  className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white"
                  onClick={() => startMarkPaid()}
                >
                  Mark paid
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-amber-600 px-2.5 py-1 text-xs font-semibold text-white"
                  onClick={() => setBulkKind('write_off')}
                >
                  Write off
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-slate-700 px-2.5 py-1 text-xs font-semibold text-white"
                  onClick={() => setBulkKind('cancel')}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-red-800 px-2.5 py-1 text-xs font-semibold text-white"
                  onClick={() => setBulkKind('void')}
                >
                  Void
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-semibold"
                  onClick={() => setBulkKind('recalc')}
                >
                  Recalculate
                </button>
              </div>

              {bulkKind ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                  <p className="font-semibold text-slate-900">
                    {selected.size} tickets ·{' '}
                    {formatCurrency(bulkKind === 'mark_paid' ? markPaidPreviewTotal : selectedBalance)}{' '}
                    will be{' '}
                    {bulkKind === 'mark_paid'
                      ? `marked paid${batchPaidAtLocal.trim() ? ` as of ${formatDateTime(fromDateTimeLocalValue(batchPaidAtLocal))}` : ' using each ticket’s last activity date'}, method: ${markMethod}`
                      : bulkKind === 'write_off'
                        ? 'written off (not revenue)'
                        : bulkKind === 'recalc'
                          ? 'recalculated from existing payments'
                          : `${bulkKind}ed with remaining balance cleared`}
                  </p>
                  {bulkKind !== 'mark_paid' ? (
                    <>
                      <button
                        type="button"
                        className="mt-1 text-xs font-semibold text-primary"
                        onClick={() => setPreviewExpand((v) => !v)}
                      >
                        {previewExpand ? 'Hide list' : 'Expand list'}
                      </button>
                      {previewExpand ? (
                        <ul className="mt-2 max-h-36 overflow-auto text-xs">
                          {selectedRows.map((r) => (
                            <li key={r.id}>
                              {r.ticketNumber} · {formatCurrency(r.balanceDue)} · {r.customerName}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </>
                  ) : null}

                  {bulkKind === 'mark_paid' ? (
                    <div className="mt-3 space-y-3">
                      <div className="flex flex-wrap items-end gap-3">
                        <label className="text-xs font-medium">
                          Method
                          <select
                            className="mt-1 block rounded-lg border border-slate-300 px-2 py-1.5"
                            value={markMethod}
                            onChange={(e) => setMarkMethod(e.target.value)}
                          >
                            <option value="other">Other</option>
                            <option value="cash">Cash</option>
                            <option value="card">Card</option>
                            <option value="check">Check</option>
                            <option value="store_credit">Store credit</option>
                          </select>
                        </label>
                        <label className="text-xs font-medium">
                          Batch date override (optional)
                          <input
                            type="datetime-local"
                            className="mt-1 block rounded-lg border border-slate-300 px-2 py-1.5"
                            value={batchPaidAtLocal}
                            onChange={(e) => setBatchPaidAtLocal(e.target.value)}
                          />
                          <span className="mt-0.5 block font-normal text-slate-500">
                            Leave blank to use each ticket’s closed_at, else created_at
                          </span>
                        </label>
                        {batchPaidAtLocal ? (
                          <button
                            type="button"
                            className="text-xs font-semibold text-slate-500 underline"
                            onClick={() => setBatchPaidAtLocal('')}
                          >
                            Clear date override
                          </button>
                        ) : null}
                        <label className="flex items-center gap-2 text-xs font-medium text-amber-800">
                          <input
                            type="checkbox"
                            checked={ackClosed}
                            onChange={(e) => setAckClosed(e.target.checked)}
                          />
                          Acknowledge closed cash-session Z-report impact if prompted
                        </label>
                      </div>

                      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                        <table className="min-w-full text-left text-xs">
                          <thead className="bg-slate-100 text-[10px] font-semibold uppercase text-slate-500">
                            <tr>
                              <th className="px-2 py-1.5">Ticket</th>
                              <th className="px-2 py-1.5">Customer</th>
                              <th className="px-2 py-1.5">Payment date</th>
                              <th className="px-2 py-1.5 text-right">Balance</th>
                              <th className="px-2 py-1.5 text-right">Amount to record</th>
                              <th className="px-2 py-1.5 text-right">Remainder</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRows.map((r) => {
                              const resolved = resolvePreviewPaidAt(r, batchPaidAtLocal);
                              const amtRaw = amountByTicket[r.id] ?? Number(r.balanceDue).toFixed(2);
                              const amt = Number(amtRaw);
                              const bal = Number(r.balanceDue);
                              const remainder =
                                Number.isFinite(amt) && amt > 0 ? Math.max(0, bal - Math.min(amt, bal)) : bal;
                              const sourceLabel =
                                resolved.source === 'batch'
                                  ? 'batch override'
                                  : resolved.source === 'closed_at'
                                    ? 'closed_at'
                                    : 'created_at (no close date)';
                              return (
                                <tr key={r.id} className="border-t border-slate-100">
                                  <td className="px-2 py-1.5 font-mono font-semibold">{r.ticketNumber}</td>
                                  <td className="max-w-[8rem] truncate px-2 py-1.5">{r.customerName}</td>
                                  <td className="px-2 py-1.5 whitespace-nowrap">
                                    <span className="font-medium text-slate-900">
                                      {formatDateTime(resolved.date.toISOString())}
                                    </span>
                                    <span className="ml-1 text-[10px] text-slate-400">({sourceLabel})</span>
                                  </td>
                                  <td className="px-2 py-1.5 text-right tabular-nums">
                                    {formatCurrency(r.balanceDue)}
                                  </td>
                                  <td className="px-2 py-1.5 text-right">
                                    <input
                                      type="number"
                                      min={0.01}
                                      max={bal}
                                      step="0.01"
                                      className="w-24 rounded border border-slate-300 px-1.5 py-0.5 text-right tabular-nums"
                                      value={amtRaw}
                                      onChange={(e) =>
                                        setAmountByTicket((prev) => ({ ...prev, [r.id]: e.target.value }))
                                      }
                                    />
                                  </td>
                                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                                    {formatCurrency(remainder)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                  {bulkKind === 'write_off' ? (
                    <label className="mt-3 block text-xs font-medium">
                      Reason
                      <input
                        className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5"
                        value={writeOffReason}
                        onChange={(e) => setWriteOffReason(e.target.value)}
                        placeholder="e.g. Import false balance"
                      />
                    </label>
                  ) : null}

                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                      onClick={() => void runBulk()}
                    >
                      Confirm with manager PIN
                    </button>
                    <button
                      type="button"
                      className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={() => setBulkKind(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table
              className="min-w-full text-left text-sm"
              tabIndex={0}
              onKeyDown={onGridKey}
            >
              <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selected.size === rows.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-3 py-3">Ticket #</th>
                  <th className="px-3 py-3">Customer</th>
                  <th className="px-3 py-3">Created</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right">Total</th>
                  <th className="px-3 py-3 text-right">Paid</th>
                  <th className="px-3 py-3 text-right">Balance</th>
                  <th className="px-3 py-3 text-right">Pay rows</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: BalanceCleanupRow) => (
                  <tr
                    key={r.id}
                    className={cn(
                      'border-t border-slate-100 hover:bg-slate-50',
                      selected.has(r.id) && 'bg-primary/5',
                      ['cancelled', 'voided'].includes(r.status) && 'bg-amber-50/40',
                    )}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(r.id)}
                        onChange={() => toggleRow(r.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <CopyTicketNumber value={r.ticketNumber} />
                      {r.externalId ? (
                        <div className="text-[10px] text-slate-400">RD #{r.externalId}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{r.customerName}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.createdAt)}</td>
                    <td className="px-3 py-2">{prettyLabel(r.status)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.total)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(r.paidTotal)}</td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums text-red-700">
                      {formatCurrency(r.balanceDue)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.paymentRows}</td>
                  </tr>
                ))}
                {rows.length === 0 && !listQ.isLoading ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-slate-400">
                      No tickets with an open balance match these filters
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {meta ? (
            <Pagination
              page={meta.page}
              pageSize={meta.pageSize}
              total={meta.total}
              onPageChange={setPage}
            />
          ) : null}
        </>
      )}

      {modalProps ? <ManagerOverrideModal {...modalProps} /> : null}
    </div>
  );
}
